// Canned email template editor (register item 9). One shared list, editable by
// anyone on the team — the model Robert chose on 2026-09-01 pending Kaycia's
// answer. Follows the ToolsView draft-row pattern rather than inventing a new
// editing idiom.
import { useMemo, useState } from 'react'
import { MERGE_FIELDS, groupTemplates } from '../lib/mergeFields.js'
import { normalizeAddressList } from '../lib/google.js'
import { ENGAGEMENT_TYPE_OPTIONS } from '../lib/format.js'
import { Button, Field } from '../ds/index.js'

const blank = { name: '', subject: '', body: '', types: [], workflow: '', step: '', order: '', cc: '', bcc: '' }

// Firestore shape → editable draft (address lists as comma text, order as text).
const toDraft = (t) => ({
  ...t,
  workflow: t.workflow || '',
  step: t.step || '',
  order: typeof t.order === 'number' ? String(t.order) : '',
  cc: (t.cc || []).join(', '),
  bcc: (t.bcc || []).join(', '),
})

// Editable draft → the fields we persist.
const fromDraft = (d) => {
  const order = Number.parseFloat(d.order)
  return {
    name: d.name, subject: d.subject, body: d.body, types: d.types || [],
    workflow: (d.workflow || '').trim(), step: (d.step || '').trim(),
    order: Number.isFinite(order) ? order : null,
    cc: normalizeAddressList(d.cc), bcc: normalizeAddressList(d.bcc),
  }
}

export default function EmailTemplatesSection({ templates = [], onCreate, onUpdate, onDelete }) {
  const [draft, setDraft] = useState(null) // null | {…} (new) | {id,…} (editing)
  const groups = useMemo(() => groupTemplates(templates), [templates])
  // Headers only once something is grouped — a flat list stays flat.
  const showHeaders = groups.some((g) => g.workflow)

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }))
  const toggleType = (value) => setDraft((d) => {
    const types = d.types || []
    return { ...d, types: types.includes(value) ? types.filter((t) => t !== value) : [...types, value] }
  })

  const save = () => {
    if (!draft?.name?.trim()) return
    if (draft.id) onUpdate(draft.id, fromDraft(draft))
    else onCreate(fromDraft(draft))
    setDraft(null)
  }

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span className="tag-label" style={{ color: 'var(--color-muted)' }}>Email templates</span>
        <div style={{ flex: 1 }} />
        <Button size="sm" onClick={() => setDraft({ ...blank })}>+ New template</Button>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--color-muted)', lineHeight: 1.5 }}>
        Shared with the whole team. Anyone can edit. Picking one while composing fills in the
        details below and shows you the finished text before it sends.
      </p>

      {draft && (
        <div style={{ display: 'grid', gap: 8, padding: 12, marginBottom: 14, background: 'var(--color-panel-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)' }}>
          <Field label="Name" value={draft.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Speaking confirmation" />
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 8 }}>
            <Field label="Workflow" value={draft.workflow || ''} onChange={(e) => set('workflow', e.target.value)} placeholder="e.g. Speaking" list="cn-template-workflows" />
            <Field label="Step" value={draft.step || ''} onChange={(e) => set('step', e.target.value)} placeholder="e.g. Phase 2" />
            <Field label="Order" type="number" value={draft.order ?? ''} onChange={(e) => set('order', e.target.value)} placeholder="100" />
          </div>
          <datalist id="cn-template-workflows">
            {groupTemplates(templates).filter((g) => g.workflow).map((g) => <option key={g.workflow} value={g.workflow} />)}
          </datalist>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Default Cc" value={draft.cc || ''} onChange={(e) => set('cc', e.target.value)} placeholder="name@careernomad.org, …" />
            <Field label="Default Bcc" value={draft.bcc || ''} onChange={(e) => set('bcc', e.target.value)} placeholder="name@careernomad.org, …" />
          </div>
          <Field label="Subject" value={draft.subject || ''} onChange={(e) => set('subject', e.target.value)} placeholder="Following up on {{engagement.title}}" />
          <Field as="textarea" label="Body" value={draft.body || ''} onChange={(e) => set('body', e.target.value)} rows={7} placeholder="Hi {{contact.firstName}},…" style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />

          <div>
            <span className="cn-field-label">Show for</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {ENGAGEMENT_TYPE_OPTIONS.map((t) => {
                const on = (draft.types || []).includes(t.value)
                return (
                  <button key={t.value} type="button" onClick={() => toggleType(t.value)}
                    style={{ background: on ? 'var(--color-gold)' : 'transparent', color: on ? 'var(--color-bg-dark)' : 'var(--color-muted)', border: '1px solid ' + (on ? 'var(--color-gold)' : 'var(--border-dash)'), borderRadius: 'var(--radius-sm)', fontSize: 12, padding: '5px 10px', cursor: 'pointer' }}>
                    {t.label}
                  </button>
                )
              })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 6 }}>
              {(draft.types || []).length === 0 ? 'Shown everywhere.' : 'Shown only on these engagement types.'}
            </div>
          </div>

          <div>
            <span className="cn-field-label">Fields you can drop in</span>
            <div style={{ fontSize: 11, color: 'var(--color-muted)', margin: '4px 0 0' }}>
              Anything else the sender must type in by hand goes in double brackets, e.g. <span className="font-mono">[[scheduling link]]</span>. Send stays locked until every one is replaced.
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {MERGE_FIELDS.map((f) => (
                <span key={f.token} title={f.label} className="font-mono"
                  style={{ fontSize: 10.5, color: 'var(--color-muted)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '3px 6px' }}>
                  {`{{${f.token}}}`}
                </span>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={!draft.name?.trim()}>Save</Button>
          </div>
        </div>
      )}

      {templates.length === 0 && !draft && (
        <div style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>No templates yet.</div>
      )}

      {groups.map((g) => (
        <div key={g.workflow || '~other'} style={{ marginBottom: showHeaders ? 12 : 0 }}>
          {showHeaders && (
            <div className="tag-label" style={{ color: 'var(--color-gold)', fontSize: 10.5, padding: '6px 0 2px' }}>{g.workflow || 'Other'}</div>
          )}
          {g.templates.map((t) => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--surface-row)' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--color-white)' }}>
                  {t.step && <span className="font-mono" style={{ fontSize: 10.5, color: 'var(--color-muted)', marginRight: 8 }}>{t.step}</span>}
                  {t.name}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--color-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.subject || 'No subject'}
                  {(t.cc || []).length > 0 && <span>{' · '}Cc {t.cc.join(', ')}</span>}
                  {(t.bcc || []).length > 0 && <span>{' · '}Bcc {t.bcc.join(', ')}</span>}
                </div>
              </div>
              <span className="font-mono" style={{ fontSize: 10.5, color: 'var(--color-muted)' }}>
                {(t.types || []).length === 0 ? 'All' : t.types.join(' · ')}
              </span>
              <button onClick={() => setDraft(toDraft(t))} className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 9px' }}>Edit</button>
              <button onClick={() => onDelete(t.id)} title="Delete template" style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 13 }}>✕</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
